import { useEffect, useRef } from "react";
import type { BgView } from "@bing/engine-backgammon";
import { playSfx } from "../sound/sfx";

/**
 * The backgammon sound cues (design spec §13). Wired ONCE inside `BackgammonTable`, so it covers
 * BOTH the solo (`BackgammonGame`) and online (`BackgammonOnlineGame`) modes — the pattern the card
 * + chess tables use.
 *
 * Every cue ADOPTS-AT-MOUNT SILENTLY: the first render only records baselines, so a mid-game RESUME
 * or a reconnect (which mounts the table with checkers already off / a roll already showing) never
 * replays a burst of sounds. Thereafter it diffs the fresh `view` against the previous one and fires:
 *
 *  - `bgHit` (loudest) when we knock an enemy checker onto the bar — it takes priority over a plain
 *    place in the same frame; else `bgCheckerPlace` when a sub-move lands (the staged count grows);
 *  - `bgBearOff` when our borne-off count rises;
 *  - `bgDiceLand` — THE GAME'S OWN DICE CLIP — once per §5.1 CONTEST throw. The contest used to be
 *    silent (it leaves `view.dice` empty, so the generic roll cue could not fire), and then a single
 *    pair-clatter played the instant the contest RESOLVED, i.e. two throws merging into one event.
 *    Each throw gets its own voice now, and that voice is the one every other roll in the game uses;
 *  - `bgDoubles` on a fresh doubles roll (four dice), else `bgDiceLand` on a fresh normal roll;
 *  - `bgGammon` (a cascade of checkers into the tray) at game-over on a مارس / سگ‌مارس
 *    (magnitude ≥ 2).
 *
 * Nothing follows a roll — the dice are the roll's only voice — and there is deliberately NO
 * «نوبت شماست» chime; see the note at the turn-change site below.
 *
 * `playSfx` already no-ops when the game sound is off, the table is muted, or the master volume is
 * zero — so this hook honours every mute and is inert in tests that don't opt sound in.
 */

interface BgSfxSnapshot {
  /** The rolled dice values joined + the staged-count — a fresh roll changes the dice part. */
  readonly rollSig: string;
  /** Sub-moves committed this turn (staged length). */
  readonly placed: number;
  /** Opponent's bar count (rises when WE hit). */
  readonly oppBar: number;
  /** OUR borne-off count. */
  readonly off: number;
  /** Is it our turn WITH dice to play (control just passed to us). */
  readonly myTurn: boolean;
  /** Whose turn it is — a change means a genuinely new roll even if the values repeat. */
  readonly turn: number;
  /** The §5.1 contest die each seat has thrown, or null while that side still owes a throw. */
  readonly contest: readonly [number | null, number | null];
}

/**
 * Each seat's contest die — read the SAME way the board reads it.
 *
 * `openingRoll` holds them while the contest is live, but it is GONE by the frame the second die
 * lands (that die is what resolves it), and the decided pair moves to `opening`. Reading only the
 * live field would leave the deciding throw silent — the exact mistake the board's own
 * `contestValues` documents.
 */
function contestDice(view: BgView): readonly [number | null, number | null] {
  const live = view.openingRoll?.dice;
  return [live?.[0] ?? view.opening?.[0] ?? null, live?.[1] ?? view.opening?.[1] ?? null];
}

function snapshot(view: BgView): BgSfxSnapshot {
  // Backgammon is strictly 2-player → narrow the seat to 0|1 ONCE, so every tuple read (`bar`/`off`
  // indexed by seat) is exact — a general `number` index would be `number | undefined` under
  // `noUncheckedIndexedAccess`.
  const you: 0 | 1 = view.you === 0 ? 0 : 1;
  const opp: 0 | 1 = you === 0 ? 1 : 0;
  return {
    rollSig: view.dice.join(",") + ":" + view.staged.length,
    placed: view.staged.length,
    oppBar: view.bar[opp],
    off: view.off[you],
    myTurn: view.turn === view.you && view.dice.length > 0,
    turn: view.turn,
    contest: contestDice(view),
  };
}

export function useBackgammonSfx(view: BgView): void {
  const first = useRef(true);
  const prev = useRef<BgSfxSnapshot>(snapshot(view));
  // Pending delayed cues, so an unmount (leaving the table) can never fire a sound into the lobby.
  const timers = useRef<number[]>([]);
  useEffect(
    () => () => {
      timers.current.forEach((id) => window.clearTimeout(id));
      timers.current = [];
    },
    [],
  );
  useEffect(() => {
    const cur = snapshot(view);
    if (first.current) {
      first.current = false;
      prev.current = cur;
      return; // silent adopt — a resume/reconnect never replays
    }
    const p = prev.current;
    const cue = (name: Parameters<typeof playSfx>[0], delayMs: number): void => {
      if (delayMs <= 0) {
        playSfx(name);
        return;
      }
      timers.current.push(window.setTimeout(() => playSfx(name), delayMs));
    };

    // ONE cue per checker event, in priority order. Bearing off used to fire `bgBearOff` AND
    // `bgCheckerPlace` together (only the hit was mutually exclusive), so a bear-off was two
    // overlapping sounds — `view.off` comes from `applyStaged`, so it rises on the STAGED move,
    // the same frame the staged count rises.
    if (cur.oppBar > p.oppBar) playSfx("bgHit");
    else if (cur.off > p.off) playSfx("bgBearOff");
    else if (cur.placed > p.placed) playSfx("bgCheckerPlace");

    /*
     * §5.1 — A CONTEST THROW. It uses `bgDiceLand`, THE GAME'S OWN DICE CLIP, unchanged.
     *
     * It used to use two bespoke single-die clips, one per side. They were the wrong instrument:
     * measured against `bg-dice-roll` (energy-weighted spectral centroid, nfft=1024, decoded by
     * Chromium's WebAudio — the app's own decoder) they read 1957 Hz / 1137 Hz against its 3768 Hz,
     * and played 7.4 / 4.2 dB LOUDER than a roll of TWO dice. «The opening dice don't sound like the
     * game's dice» — because they were a different sound at the wrong level. The simplest cure is
     * not to build a better single-die clip, it is to stop having one.
     *
     * ⚠️ FIRED AT 0, NOT `DIE_CONTACT_MS`. This clip is a recording of dice TUMBLING and then
     * landing — its dominant impact is 736 ms in — so it starts with the throw, exactly like the
     * pair cues below. The visual contest die contacts the felt at `DIE_CONTACT_MS` (700 ms), so
     * the audible landing arrives within 36 ms of the visible one. Delaying it by another 700 ms
     * (which the single-die clips correctly needed, being a landing and nothing else) would put
     * the clatter most of a second after the die had already stopped.
     *
     * ⚠️ ONE CUE PER FRAME, not one per die. Two dice cannot land in the same frame in normal play
     * (measured on the deployed server: the two throws are 2474 ms apart, and the clip is 1333 ms,
     * so they never overlap either) — but a coalesced frame must not fire the same clip twice on
     * top of itself.
     */
    const landed = ([0, 1] as const).filter(
      (seat) => cur.contest[seat] !== null && p.contest[seat] === null,
    );
    if (landed.length > 0) cue("bgDiceLand", 0);

    // A fresh roll: doubles (four dice) get the fanfare, otherwise the dice-land clatter.
    // Keyed on the DICE VALUES (+ a turn change), never on `rollSig` — that embeds the staged
    // count, so undoing your last sub-move (staged 1 -> 0, dice unchanged) read as a brand-new
    // roll and played the dice clatter at you. A turn change still counts, so an opponent who
    // happens to roll the same pair is not silent.
    const rolled = view.dice.join(",");
    const prevRolled = p.rollSig.split(":")[0];
    const rollChanged = rolled !== prevRolled || cur.turn !== p.turn;
    const freshDoubles = view.dice.length === 4 && rollChanged;
    const freshRoll = view.dice.length > 0 && view.staged.length === 0 && rollChanged;

    // The dice clips play from the START of the throw, not at contact: each is a recording of
    // dice tumbling and THEN landing, with the impact ~0.70s in — the same moment the dice hit
    // the felt on screen. Played at contact instead, the audible clatter arrived ~950ms after
    // the dice had already stopped.
    //
    // ⚠️ …EXCEPT on the frame the contest RESOLVES. `view.dice` goes []→[a,b] there — the Iranian
    // rule plays the two contest dice as the starter's first roll — so the generic pair-clatter
    // used to fire on top of the second single-die throw. That is precisely the "two throws merged
    // into one event" this section fixes; the two single-die cues above have already voiced them.
    // (The board withholds the tray for the same reason: one pair of dice on screen, one in the ear.)
    if (landed.length === 0) {
      if (freshDoubles) cue("bgDoubles", 0);
      else if (freshRoll) cue("bgDiceLand", 0);
    }

    // ⚠️ NO «نوبت شماست» CHIME (removed 2026-08-15, the operator's call). It was `notify.mp3` —
    // the app's UI/notification tone, borrowed, and it does not belong in تخته نرد: the board
    // already says whose turn it is (the ring, the hint, «تاس بریز» lighting up), and the dice
    // announce the only turn that needs announcing — yours, the moment you act on it. منچ dropped
    // the same cue for the same reason.
    //
    // A dance/blocked roll gets no extra sound either — the board and the hint already say it.
    if (view.status === "over" && (view.magnitude ?? 1) >= 2) playSfx("bgGammon");

    prev.current = cur;
  }, [view]);
}
